import {
  AlertTriangle,
  CalendarCheck,
  CheckCircle2,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  LockKeyhole,
  ShieldAlert,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { ClosedBusinessDay } from '@/api/ledger-types'
import { session } from '@/api/session'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { MoneyDisplay } from '@/components/money-display'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { branchLabel } from '@/lib/branch'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'
import type { ApiError } from '@/lib/problem-details'

import { BranchReachNotice, BranchSelect } from './branch-reach'
import { formatMonthOf, PERIOD_STATUS_LABEL, PERIOD_STATUS_MEANING, today } from './constants'
import { LedgerDualControlOutcome } from './dual-control'
import { obstructionsFromError, type LedgerObstruction } from './ledger-errors'
import { useCloseBusinessDay } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import {
  useBlockingDriftAlerts,
  useBranchBusinessDay,
  useLedgerBranchReach,
  usePostingPeriods,
} from './queries'

/**
 * Closing a branch's trading day.
 *
 * This is the control that turns a day's postings into a reported figure. Until it runs, the day is
 * a set of entries somebody could still add to; after it runs, the day's balances are what the
 * institution says happened, and every downstream number — the trial balance, the month's period
 * close, the Bank of Ghana return — is built on them.
 *
 * **The refusal is the screen's most important output, not an error.** A day close is refused for a
 * *set* of reasons, and hiding that set in a toast is how a branch manager spends the last hour of
 * the day discovering them one at a time. So every obstruction the server names is rendered as its
 * own line with the step that clears it, and the panel stays on screen until something changes.
 *
 * Two kinds of obstruction are shown and they are labelled differently on purpose:
 *
 * - **What the server said.** Authoritative. It refused, and it named these.
 * - **What this screen checked.** The pre-flight below, run from this client's own reads before the
 *   click — the day's own status, the unresolved drift alarms standing against it, and the state of
 *   the accounting period it falls in. These are the obstructions that can be found without asking,
 *   and finding them early is worth a great deal at half past five. They are never presented as the
 *   server's verdict, because the contract publishes no readiness endpoint: the close endpoint's
 *   refusal is the only authority on whether a day can close.
 *
 * **Width policy.** The whole section refuses below `lg` — `docs/mobile-policy.md`. Nothing here
 * keys an amount, but the obstruction list is the point of the screen and it is a list somebody has
 * to be able to read in full, beside the day it belongs to, before making a decision that is
 * expensive to take back. A reopening exists (`reopen-business-day.tsx`) but it is countersigned
 * and fenced to the branch's most recent day — a fresh checker, not an undo button.
 */
export function CloseBusinessDaySection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const [chosenBranchId, setChosenBranchId] = useState<string | null>(null)
  const [chosenDate, setChosenDate] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [closed, setClosed] = useState<ClosedBusinessDay | null>(null)

  const close = useCloseBusinessDay()

  const branchId = chosenBranchId ?? session.getBranchId() ?? reach.homeBranchId
  const selected = reach.branches.find((branch) => branch.id === branchId) ?? null

  const day = useBranchBusinessDay(selected?.id ?? '')

  /*
   * The date defaults to the branch's *own* business date rather than to today. That is the whole
   * point of a business day: a branch that has not closed Friday is still working in Friday on
   * Monday morning, and defaulting to the browser's date would offer to close a day the branch is
   * not in. The operator may still override it — closing a day that was left open last week is
   * exactly the job this screen exists for.
   */
  const businessDate = chosenDate ?? day.data?.businessDate ?? null

  const periods = usePostingPeriods({ take: 36 }, permissions.canCloseDay)
  const drift = useBlockingDriftAlerts(
    selected?.id ?? null,
    businessDate,
    permissions.canCloseDay && permissions.canViewDrift,
  )

  if (!permissions.canCloseDay) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockKeyhole className="size-4 text-muted" aria-hidden />
            Closing a day
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-body">
            Closing a trading day needs{' '}
            <span className="font-mono text-xs">{LEDGER_PERMISSIONS.dayClose}</span>, which your role
            does not hold. That is the ordinary arrangement — the close is what makes a day&apos;s
            postings a reported figure, and it belongs to the branch manager or the accountant.
          </p>
        </CardContent>
      </Card>
    )
  }

  const period =
    businessDate === null
      ? null
      : (periods.data?.find(
          (candidate) => candidate.startDate <= businessDate && businessDate <= candidate.endDate,
        ) ?? null)

  const unresolvedAlerts = drift.data?.items ?? []

  /*
   * The pre-flight. Each entry is a question this client can answer from a read it has already
   * made, and each carries the step that clears it. `blocking: false` entries are still shown —
   * "nothing standing in the way here" is information at the end of a day, and a checklist that
   * renders only its failures leaves the reader unable to tell a passed check from one that was
   * never run.
   */
  const preflight: PreflightCheck[] = []

  if (selected !== null && businessDate !== null) {
    const status = day.data?.status ?? null
    preflight.push({
      id: 'day-open',
      label: 'The day is open',
      state: day.isPending ? 'unknown' : status === 'Open' || status === 'Closing' ? 'clear' : 'blocked',
      detail:
        status === 'Open'
          ? `${branchLabel(selected)} is trading on ${formatBusinessDate(businessDate)}.`
          : status === 'Closing'
            ? 'The day is already closing — it accepts no new postings and the close may be completed.'
            : 'This branch has no open day. There is nothing to close.',
      remedy:
        'Open the day on the business-days screen if it should be trading, or check the date: this may be a day that was already closed.',
    })

    preflight.push({
      id: 'drift',
      label: 'No unresolved balance drift',
      state: !permissions.canViewDrift
        ? 'unknown'
        : drift.isPending
          ? 'unknown'
          : unresolvedAlerts.length === 0
            ? 'clear'
            : 'blocked',
      detail: !permissions.canViewDrift
        ? `Reading the drift register needs ${LEDGER_PERMISSIONS.driftView}, which your role does not hold — so this screen cannot check it for you. The close still checks it.`
        : unresolvedAlerts.length === 0
          ? 'Nothing is standing against this branch and date.'
          : `${String(unresolvedAlerts.length)} alarm${unresolvedAlerts.length === 1 ? '' : 's'} against this branch and date. An unresolved alarm means a reported figure may be wrong.`,
      remedy:
        'Open the balance drift register, look at each alarm, and dispose of it — repaired, or accepted with an explanation. A day will not close over one.',
      // The alarms themselves, not just their count. Somebody at half past five needs to know which
      // account is out and by how much before deciding whether this is a five-minute job.
      extra: unresolvedAlerts.length > 0 ? <BlockingDriftSummary alerts={unresolvedAlerts} /> : undefined,
    })

    preflight.push({
      id: 'period',
      label: 'The accounting period accepts it',
      state: periods.isPending
        ? 'unknown'
        : period === null
          ? 'unknown'
          : period.status === 'HardClosed'
            ? 'blocked'
            : 'clear',
      detail:
        period === null
          ? `No period was found covering ${formatBusinessDate(businessDate)} in the last three years of the register.`
          : `${formatMonthOf(businessDate)} is ${PERIOD_STATUS_LABEL[period.status].toLowerCase()}. ${PERIOD_STATUS_MEANING[period.status]}`,
      remedy:
        'A sealed period has to be reopened before anything moves inside it, and reopening is countersigned. That is a month-end decision, not a day-end one.',
    })
  }

  const blocked = preflight.filter((check) => check.state === 'blocked')
  const submittable = selected !== null && businessDate !== null && !close.isPending

  return (
    <RequiresWiderScreen
      screen="Closing a business day"
      reason="It turns a day's postings into a reported figure, nothing in this application reopens a closed day, and what the server refuses with is a list that has to be read in full beside the day it belongs to."
    >
      <div className="space-y-4">
        <BranchReachNotice reach={reach} />

        <Card>
          <CardHeader>
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck className="size-4 text-brand-text" aria-hidden />
                Close a business day
              </CardTitle>
              <p className="mt-0.5 text-xs text-muted">
                The close runs an integrity check on the way through and carries the day&apos;s
                balances forward. It may be countersigned — the answer says which.
              </p>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <BranchSelect
                id="close-day-branch"
                label="Branch"
                branches={reach.branches}
                value={selected?.id ?? null}
                onChange={(next) => {
                  setChosenBranchId(next)
                  setChosenDate(null)
                  setClosed(null)
                  close.reset()
                }}
                description="Only branches the server has confirmed you may act in are offered."
              />

              <div className="space-y-1.5">
                <Label htmlFor="close-day-date">Business date</Label>
                <Input
                  id="close-day-date"
                  type="date"
                  value={businessDate ?? ''}
                  max={today()}
                  onChange={(event) => {
                    setChosenDate(event.target.value)
                    setClosed(null)
                    close.reset()
                  }}
                  aria-describedby="close-day-date-help"
                />
                <p id="close-day-date-help" className="text-xs text-muted">
                  {day.data?.businessDate
                    ? `Defaults to the branch's own trading date, ${formatBusinessDate(day.data.businessDate)} — not to today. A branch that has not closed Friday is still working in Friday.`
                    : 'The branch has no open day, so there is no date to default to.'}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="close-day-note">Carried-forward note (optional)</Label>
              <textarea
                id="close-day-note"
                rows={2}
                value={note}
                placeholder="Anything the next day's operator should know."
                onChange={(event) => setNote(event.target.value)}
                aria-describedby="close-day-note-help"
                className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
              />
              <p id="close-day-note-help" className="text-xs text-muted">
                Stored on the day in the register and read by whoever looks at it afterwards. A clean
                close needs no explanation, so this is genuinely optional.
              </p>
            </div>

            <PreflightPanel checks={preflight} isPending={day.isPending || periods.isPending} />

            {close.isError ? <DayCloseRefusal error={close.error} /> : null}

            {closed ? <DayCloseOutcome result={closed} /> : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                disabled={!submittable}
                onClick={() => setConfirming(true)}
              >
                {close.isPending ? 'Closing…' : 'Close the day'}
              </Button>
              {blocked.length > 0 ? (
                <p className="text-xs text-warning-text">
                  {blocked.length === 1
                    ? 'One check above is standing in the way.'
                    : `${String(blocked.length)} checks above are standing in the way.`}{' '}
                  The button is still live — this screen&apos;s checks are advisory and the server is
                  the authority.
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Close this trading day"
          description={
            selected && businessDate
              ? `${branchLabel(selected)} on ${formatBusinessDate(businessDate)}. This turns the day's postings into a reported figure.`
              : 'Choose a branch and a date first.'
          }
          confirmLabel="Close the day"
          confirmationPhrase="CLOSE"
          tone="danger"
          isPending={close.isPending}
          onConfirm={() => {
            if (selected === null || businessDate === null) {
              return
            }

            close.mutate(
              {
                // The identifier is a string throughout this application and an int64 on the
                // endpoint, so it converts here, at the edge. It is the selected branch's own
                // identifier — never a value this screen has not shown.
                branchId: Number(selected.id),
                businessDate,
                note: note.trim() === '' ? null : note.trim(),
              },
              {
                onSuccess: (result) => {
                  setClosed(result)
                  setConfirming(false)
                },
                /*
                 * The dialog closes on a refusal too, and that is the point rather than an
                 * oversight: the obstruction list behind it is the screen's most important output,
                 * and leaving a modal over it would reduce a refusal that names four things to the
                 * one line a dialog has room for.
                 */
                onError: () => setConfirming(false),
              },
            )
          }}
        >
          <Alert tone="warning">
            <AlertTriangle aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Nothing reopens a closed day</AlertTitle>
              <AlertDescription>
                There is no endpoint that reopens one, and the day&apos;s balances are carried forward
                as it closes. Check the branch and the date above before confirming.
              </AlertDescription>
            </div>
          </Alert>

          {blocked.length > 0 ? (
            <Alert tone="danger">
              <ShieldAlert aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This will probably be refused</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    {blocked.map((check) => (
                      <li key={check.id}>{check.label}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </ConfirmDialog>
      </div>
    </RequiresWiderScreen>
  )
}

interface PreflightCheck {
  id: string
  label: string
  /** `unknown` means this client could not answer — a pending read, or a permission it lacks. */
  state: 'clear' | 'blocked' | 'unknown'
  detail: string
  remedy: string
  /** Anything worth showing under a blocked check — the alarms themselves, not only their count. */
  extra?: ReactNode
}

/**
 * What this screen could work out before the click.
 *
 * Marked as the client's own reads throughout. The contract publishes no readiness endpoint for the
 * close, so nothing here is the server's verdict — a check that reads clear is not a promise, and
 * the close is what settles it.
 */
function PreflightPanel({ checks, isPending }: { checks: PreflightCheck[]; isPending: boolean }) {
  if (checks.length === 0) {
    return (
      <p className="text-sm text-muted">
        Choose a branch and a date to see what is standing in the way of closing it.
      </p>
    )
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold tracking-wide text-muted uppercase">Before you close</p>
        <span className="text-2xs text-muted">
          Checked by this screen, not by the server. The close is what decides.
        </span>
      </div>

      {isPending ? (
        <div className="mt-2 space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <ul className="mt-2 space-y-2">
          {checks.map((check) => (
            <li key={check.id} className="flex gap-2">
              {check.state === 'clear' ? (
                <CircleCheck className="mt-0.5 size-4 shrink-0 text-success-text" aria-hidden />
              ) : check.state === 'blocked' ? (
                <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-text" aria-hidden />
              ) : (
                <CircleDashed className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-heading">{check.label}</p>
                <p className="text-xs text-body">{check.detail}</p>
                {check.state === 'blocked' ? (
                  <>
                    <p className="mt-0.5 text-xs text-warning-text">{check.remedy}</p>
                    {check.extra ? <div className="mt-1.5">{check.extra}</div> : null}
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A refused close, in full.
 *
 * Every obstruction the server named gets its own line and its own remedy. This is a panel rather
 * than a toast and it does not dismiss itself: an operator has to be able to work down it, clear
 * each item, and come back — and a message that has already faded cannot be worked down.
 */
export function DayCloseRefusal({ error }: { error: ApiError }) {
  const obstructions: LedgerObstruction[] = obstructionsFromError(error)

  return (
    <Alert tone="danger">
      <ShieldAlert aria-hidden />
      <div className="min-w-0 space-y-2">
        <AlertTitle>
          The day was not closed
          {obstructions.length > 1 ? ` — ${String(obstructions.length)} things are in the way` : ''}
        </AlertTitle>
        <AlertDescription>
          Nothing has changed: the branch is still trading on that date, and every posting made today
          is still in the day. Clear the items below and close again.
        </AlertDescription>

        <ol className="space-y-2">
          {obstructions.map((obstruction) => (
            <li
              key={obstruction.id}
              className="rounded-control border border-danger-border bg-surface p-2"
            >
              <p className="text-sm font-medium text-heading">{obstruction.title}</p>
              <p className="mt-0.5 text-xs text-body">{obstruction.remedy}</p>
            </li>
          ))}
        </ol>

        <p className="font-mono text-2xs text-muted">
          {error.code ? <span>{error.code}</span> : null}
          {error.code && error.correlationId ? <span aria-hidden> · </span> : null}
          {error.correlationId ? <span>ref {error.correlationId}</span> : null}
        </p>
      </div>
    </Alert>
  )
}

/**
 * What the close actually did — or what it queued.
 *
 * The dual-control envelope comes first because it is the fact everything else depends on. Beneath
 * it are the two things a closer needs to see afterwards: the integrity check the close ran, and
 * whatever it carried into the next day.
 */
function DayCloseOutcome({ result }: { result: ClosedBusinessDay }) {
  return (
    <div className="space-y-3">
      <LedgerDualControlOutcome
        result={result}
        awaitingDescription={
          <>
            The close of {formatBusinessDate(result.businessDate)} is stored and waiting for a
            countersignature. <strong>The branch is still trading on that date</strong> and postings
            are still being accepted into it.
          </>
        }
        appliedDescription={
          <>
            {formatBusinessDate(result.businessDate)} is closed
            {result.closedAtUtc ? (
              <>
                {' '}
                as at{' '}
                <span className="numeric tabular-nums">{formatDateTime(result.closedAtUtc)}</span>
              </>
            ) : null}
            . Its balances are now the reported figures for that day.
          </>
        }
      >
        <p className="text-xs text-body">
          Integrity check: <span className="numeric tabular-nums">{result.rollupsChecked}</span> rollup
          {result.rollupsChecked === 1 ? '' : 's'} re-derived from the postings,{' '}
          <span className="numeric tabular-nums">{result.rollupsRepaired}</span> repaired.
          {result.rollupsRepaired > 0 ? (
            <>
              {' '}
              A repaired rollup still raises an alarm in the drift register for the record — it is
              worth reading why it drifted.
            </>
          ) : null}
        </p>
      </LedgerDualControlOutcome>

      {result.carriedForward.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-success-text" aria-hidden />
              Carried forward
            </CardTitle>
            <Badge tone="neutral">{result.carriedForward.length}</Badge>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm text-body">
              {result.carriedForward.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {result.carriedForwardNote ? (
              <p className="mt-2 border-t border-border-subtle pt-2 text-xs text-muted">
                Note: {result.carriedForwardNote}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

/**
 * The unresolved alarms standing against one day, shown on the close screen itself.
 *
 * Exported so the drift screen and the close screen render an alarm the same way — the figures on
 * it are money in integer pesewas and go through {@link MoneyDisplay}, never through a hand-rolled
 * division by a hundred.
 */
export function BlockingDriftSummary({
  alerts,
}: {
  alerts: { publicId: string; subject: string; subjectKey: string; differenceMinorUnits: number }[]
}) {
  if (alerts.length === 0) {
    return null
  }

  return (
    <ul className="space-y-1">
      {alerts.map((alert) => (
        <li key={alert.publicId} className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-mono text-xs text-heading">
            {alert.subject} · {alert.subjectKey}
          </span>
          <MoneyDisplay
            value={alert.differenceMinorUnits}
            signed
            colourNegative
            className="text-xs"
          />
        </li>
      ))}
    </ul>
  )
}
